// Drift Auditor -- premium "AI Regime-Aware Dynamic Rebalancing (PRO)" showcase.
//
// A standalone, deliberately eye-catching upsell island that highlights how the
// PRO tier beats the three static disciplines on Ending Wealth, CAGR, Sharpe and
// Max Drawdown. Driven entirely by the strategy_comparison rows the API returns
// (the PRO row carries is_locked_pro_tier = true).

import React, { useMemo } from 'react';
import { ArrowRight, Crown, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';

import type { StrategyMetricsRow } from '../types';

const UPGRADE_URL = 'https://www.ki-wealth.com/membership-levels';

const fmtMoney = (v: number | null): string =>
    v == null ? '—' : `$${Math.round(v).toLocaleString('en-US')}`;
const fmtMoneyShort = (v: number): string => {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '+';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
};
const fmtPct = (v: number | null): string => (v == null ? '—' : `${v.toFixed(2)}%`);

interface MetricTileProps {
    label: string;
    value: string;
    delta: string;
    deltaGood: boolean;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, delta, deltaGood }) => (
    <div className="rounded-xl bg-white/[0.06] backdrop-blur-sm border border-white/10 p-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-200/70">
            {label}
        </div>
        <div className="mt-1 text-2xl font-extrabold text-white tabular-nums">{value}</div>
        <div
            className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${
                deltaGood ? 'text-emerald-300' : 'text-rose-300'
            }`}
        >
            <TrendingUp className="h-3 w-3" />
            {delta} <span className="text-indigo-200/50 font-medium">vs best static</span>
        </div>
    </div>
);

export const ProRebalancingShowcase: React.FC<{ rows: StrategyMetricsRow[] }> = ({ rows }) => {
    const { pro, edges } = useMemo(() => {
        const proRow = rows.find((r) => r.is_locked_pro_tier) ?? null;
        const statics = rows.filter((r) => !r.is_locked_pro_tier);

        const safeMax = (vals: Array<number | null>, fallback = 0): number => {
            const nums = vals.filter((v): v is number => v != null);
            return nums.length ? Math.max(...nums) : fallback;
        };

        const bestWealth = safeMax(statics.map((s) => s.final_wealth));
        const bestCagr = safeMax(statics.map((s) => s.cagr_pct));
        const bestSharpe = safeMax(statics.map((s) => s.sharpe_ratio));
        // Max Drawdown values are negative; the "shallowest" (best) is the max.
        const shallowestDD = safeMax(statics.map((s) => s.max_drawdown_pct), -0.0001);

        return {
            pro: proRow,
            edges: {
                wealth: (proRow?.final_wealth ?? 0) - bestWealth,
                cagr: (proRow?.cagr_pct ?? 0) - bestCagr,
                sharpe: (proRow?.sharpe_ratio ?? 0) - bestSharpe,
                dd: (proRow?.max_drawdown_pct ?? 0) - shallowestDD, // positive = shallower
            },
        };
    }, [rows]);

    if (!pro) return null;

    return (
        <div className="relative overflow-hidden rounded-2xl border border-indigo-400/20 shadow-xl shadow-indigo-950/30 bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-950">
            {/* Decorative glow accents */}
            <div className="pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />

            <div className="relative p-6 sm:p-7">
                {/* Header */}
                <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 px-3 py-1 text-xs font-black uppercase tracking-widest text-amber-950 shadow-sm">
                        <Crown className="h-3.5 w-3.5" /> PRO
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-300">
                        <Sparkles className="h-3 w-3" /> Ranked #1 Across Every Metric
                    </span>
                </div>

                <h3 className="mt-4 text-xl sm:text-2xl font-extrabold text-white leading-tight">
                    Forge Ultra Adaptive Advantage™
                </h3>
                <p className="mt-1.5 max-w-2xl text-sm text-indigo-100/70">
                    Many investors unknowingly hold portfolios that no longer match today's
                    market environment. Forge Ultra helps uncover where adjustments may be
                    beneficial, highlighting potential opportunities to strengthen
                    diversification, manage risk, and position your portfolio for changing
                    market conditions.
                </p>

                {/* Metric tiles */}
                <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <MetricTile
                        label="Ending Wealth"
                        value={fmtMoney(pro.final_wealth)}
                        delta={fmtMoneyShort(edges.wealth)}
                        deltaGood={edges.wealth >= 0}
                    />
                    <MetricTile
                        label="CAGR"
                        value={fmtPct(pro.cagr_pct)}
                        delta={`${edges.cagr >= 0 ? '+' : ''}${edges.cagr.toFixed(2)}%`}
                        deltaGood={edges.cagr >= 0}
                    />
                    <MetricTile
                        label="Sharpe Ratio"
                        value={pro.sharpe_ratio == null ? '—' : pro.sharpe_ratio.toFixed(2)}
                        delta={`${edges.sharpe >= 0 ? '+' : ''}${edges.sharpe.toFixed(2)}`}
                        deltaGood={edges.sharpe >= 0}
                    />
                    <MetricTile
                        label="Max Drawdown"
                        value={fmtPct(pro.max_drawdown_pct)}
                        delta={`${edges.dd >= 0 ? '+' : ''}${edges.dd.toFixed(2)}%`}
                        deltaGood={edges.dd >= 0}
                    />
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <a
                        href={UPGRADE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 px-6 py-3 text-sm font-black uppercase tracking-wide text-amber-950 shadow-lg shadow-amber-500/20 transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                        Reveal My Portfolio Opportunities
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                    <p className="inline-flex items-center gap-1.5 text-xs text-indigo-100/60">
                        <ShieldCheck className="h-4 w-4 text-emerald-300" />
                        Point-in-time, look-ahead-safe backtest · net of your transaction costs
                    </p>
                </div>

                {/* Professional risk & educational disclosure */}
                <p className="mt-5 border-t border-white/10 pt-4 text-[11px] leading-relaxed text-slate-400">
                    Please note that investing implies high risk. Forge Ultra rebalancing does not
                    guarantee consistently superior returns, which depend on many macroeconomic
                    factors. All information presented on Forge Ultra platform is for educational
                    and informational purposes only.
                </p>
            </div>
        </div>
    );
};
